import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Search,
  Clear,
  Folder,
  FolderOpen,
  Description,
  PictureAsPdf,
  TableChart,
  Image as ImageIcon,
  Download,
  Launch,
  Send,
  ExpandMore,
  ExpandLess,
  HelpOutline,
  Article,
  CheckCircleOutline,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { CorporateDocumentService } from 'CorporateDocument/Domain/Services/CorporateDocumentService';

// Helper to determine the icon based on file extension
const getDocumentIcon = (fileName = '') => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf':
      return <PictureAsPdf sx={{ color: '#e11d48', fontSize: '1.35rem' }} />;
    case 'xls':
    case 'xlsx':
    case 'csv':
      return <TableChart sx={{ color: '#16a34a', fontSize: '1.35rem' }} />;
    case 'doc':
    case 'docx':
      return <Description sx={{ color: '#257eb9', fontSize: '1.35rem' }} />;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'webp':
      return <ImageIcon sx={{ color: '#9333ea', fontSize: '1.35rem' }} />;
    default:
      return <Article sx={{ color: '#64748b', fontSize: '1.35rem' }} />;
  }
};

export default function CorporateDocumentsView() {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Petition state
  const [petitionMessage, setPetitionMessage] = useState('');
  const [submittingPetition, setSubmittingPetition] = useState(false);
  const [petitionSuccess, setPetitionSuccess] = useState(false);

  // Load tree
  const loadTree = useCallback(async () => {
    try {
      setLoading(true);
      const res = await CorporateDocumentService.getTree();
      if (res && res.categories) {
        setCategories(res.categories);
      }
    } catch (err) {
      toast.error('Error al cargar el listado de documentos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  // Toggle category expand/collapse
  const toggleCategory = (catId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [catId]: !prev[catId],
    }));
  };

  // Filter tree recursively based on searchTerm
  const filteredTree = useMemo(() => {
    if (!searchTerm.trim()) {
      return categories;
    }
    const term = searchTerm.toLowerCase();

    const filterCategory = (cat) => {
      const matchCat = cat.name.toLowerCase().includes(term);

      // Filter matching documents
      const matchingDocs = (cat.documents || []).filter((d) =>
        d.name.toLowerCase().includes(term) || d.filePath?.toLowerCase().includes(term)
      );

      // Filter children
      const matchingChildren = (cat.children || [])
        .map((child) => filterCategory(child))
        .filter((child) => child !== null);

      if (matchCat || matchingDocs.length > 0 || matchingChildren.length > 0) {
        return {
          ...cat,
          documents: matchCat ? cat.documents : matchingDocs,
          children: matchingChildren,
        };
      }

      return null;
    };

    return categories.map((cat) => filterCategory(cat)).filter((cat) => cat !== null);
  }, [categories, searchTerm]);

  // Auto-expand categories when searching
  useEffect(() => {
    if (searchTerm.trim()) {
      const autoExpand = {};
      const collectIds = (list) => {
        list.forEach((item) => {
          autoExpand[item.id] = true;
          if (item.children && item.children.length > 0) {
            collectIds(item.children);
          }
        });
      };
      collectIds(filteredTree);
      setExpandedCategories(autoExpand);
    }
  }, [searchTerm, filteredTree]);

  // Document selection
  const handleSelectDoc = (doc) => {
    if (selectedDoc?.id !== doc.id) {
      setPreviewLoading(true);
    }
    setSelectedDoc(doc);
  };

  // Submit Petition
  const handleSubmitPetition = async (e) => {
    e.preventDefault();
    if (!petitionMessage.trim()) {
      toast.warn('Por favor, escribe el detalle de tu petición.');
      return;
    }

    try {
      setSubmittingPetition(true);
      const res = await CorporateDocumentService.sendPetition(petitionMessage);
      if (res && res.success) {
        setPetitionSuccess(true);
        setPetitionMessage('');
        toast.success('Petición enviada correctamente al Departamento de Marketing.');
        setTimeout(() => setPetitionSuccess(false), 8000);
      } else {
        toast.error(res?.message || 'Error al enviar la petición.');
      }
    } catch (err) {
      toast.error('Error al enviar la petición. Inténtalo de nuevo.');
    } finally {
      setSubmittingPetition(false);
    }
  };

  // Recursive Category Item Component
  const renderCategoryItem = (cat, depth = 0) => {
    const isExpanded = !!expandedCategories[cat.id];
    const hasChildren = (cat.children && cat.children.length > 0);
    const hasDocs = (cat.documents && cat.documents.length > 0);
    const isEmpty = !hasChildren && !hasDocs;

    return (
      <Box key={cat.id} sx={{ mb: depth === 0 ? 1 : 0.5 }}>
        {/* Category Header */}
        <ListItemButton
          onClick={() => toggleCategory(cat.id)}
          sx={{
            py: 1.1,
            px: 1.5,
            pl: depth > 0 ? depth * 2.5 + 1.5 : 1.5,
            borderRadius: '10px',
            bgcolor: depth === 0 ? (isExpanded ? '#f0f7fd' : '#ffffff') : 'transparent',
            border: depth === 0 ? '1px solid' : 'none',
            borderColor: isExpanded ? '#b9ddf8' : '#e2e8f0',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: '#eaf4fd',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            {isExpanded ? (
              <FolderOpen sx={{ color: '#257eb9', fontSize: depth === 0 ? '1.5rem' : '1.3rem' }} />
            ) : (
              <Folder sx={{ color: '#257eb9', fontSize: depth === 0 ? '1.5rem' : '1.3rem' }} />
            )}
          </ListItemIcon>

          <ListItemText
            primary={cat.name}
            primaryTypographyProps={{
              fontWeight: depth === 0 ? 600 : 500,
              fontSize: depth === 0 ? '0.95rem' : '0.9rem',
              color: '#1e293b',
            }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {cat.documentCount > 0 && (
              <Chip
                label={`${cat.documentCount} ${cat.documentCount === 1 ? 'doc' : 'docs'}`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  bgcolor: isExpanded ? '#257eb9' : '#e2e8f0',
                  color: isExpanded ? '#ffffff' : '#475569',
                }}
              />
            )}
            {!isEmpty && (
              isExpanded ? (
                <ExpandLess sx={{ color: '#64748b', fontSize: '1.25rem' }} />
              ) : (
                <ExpandMore sx={{ color: '#64748b', fontSize: '1.25rem' }} />
              )
            )}
          </Box>
        </ListItemButton>

        {/* Category Body */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              pl: depth > 0 ? 1 : 1.5,
              ml: depth > 0 ? depth * 1.5 : 1.5,
              borderLeft: '2px solid #e2e8f0',
              mt: 0.5,
              mb: 1,
            }}
          >
            {/* Subcategories */}
            {cat.children && cat.children.map((child) => renderCategoryItem(child, depth + 1))}

            {/* Documents */}
            {hasDocs && (
              <List disablePadding sx={{ mt: cat.children?.length > 0 ? 0.5 : 0 }}>
                {cat.documents.map((doc) => {
                  const isSelected = selectedDoc?.id === doc.id;
                  const downloadUrl = CorporateDocumentService.getDocumentFileUrl(doc.id, true);

                  return (
                    <ListItemButton
                      key={doc.id}
                      onClick={() => handleSelectDoc(doc)}
                      sx={{
                        py: 0.8,
                        px: 1.5,
                        borderRadius: '8px',
                        mb: 0.4,
                        bgcolor: isSelected ? '#e0f2fe' : 'transparent',
                        border: isSelected ? '1px solid #7dd3fc' : '1px solid transparent',
                        borderLeft: isSelected ? '4px solid #0284c7' : '1px solid transparent',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          bgcolor: isSelected ? '#bae6fd' : '#f8fafc',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {getDocumentIcon(doc.filePath || doc.name)}
                      </ListItemIcon>

                      <ListItemText
                        primary={doc.name}
                        primaryTypographyProps={{
                          fontSize: '0.86rem',
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? '#0369a1' : '#334155',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      />

                      <Tooltip title="Descargar documento">
                        <IconButton
                          size="small"
                          href={downloadUrl}
                          download
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            color: '#64748b',
                            '&:hover': { color: '#0284c7', bgcolor: '#e0f2fe' },
                          }}
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemButton>
                  );
                })}
              </List>
            )}

            {isEmpty && (
              <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic', pl: 2, py: 0.5, display: 'block' }}>
                Carpeta vacía
              </Typography>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, maxWidth: 1600, margin: '0 auto' }}>
      <Grid container spacing={3}>
        {/* LEFT COLUMN: Categories Tree & Search */}
        <Grid item xs={12} lg={7} xl={7.5}>
          <Card
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              bgcolor: '#ffffff',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)',
              overflow: 'hidden',
            }}
          >
            {/* Search Bar */}
            <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar documentos o carpetas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  sx: {
                    borderRadius: '10px',
                    bgcolor: '#ffffff',
                    fontSize: '0.9rem',
                    '& fieldset': { borderColor: '#cbd5e1' },
                    '&:hover fieldset': { borderColor: '#257eb9' },
                    '&.Mui-focused fieldset': { borderColor: '#257eb9' },
                  },
                }}
              />
              {searchTerm && (
                <Typography variant="caption" sx={{ color: '#64748b', mt: 0.8, display: 'block' }}>
                  Resultados filtrados para: <strong>"{searchTerm}"</strong>
                </Typography>
              )}
            </Box>

            {/* Tree Content */}
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, maxHeight: '72vh', overflowY: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                  <CircularProgress size={36} sx={{ color: '#257eb9' }} />
                  <Typography variant="body2" sx={{ mt: 2, color: '#64748b' }}>
                    Cargando estructura de documentos...
                  </Typography>
                </Box>
              ) : filteredTree.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Folder sx={{ fontSize: 52, color: '#cbd5e1', mb: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#475569' }}>
                    {searchTerm ? 'No se encontraron documentos ni carpetas' : 'No hay documentos disponibles'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                    {searchTerm
                      ? 'Prueba con otros términos de búsqueda o solicita el documento abajo a la derecha.'
                      : 'Actualmente no tienes documentos asignados.'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: 0 }}>
                  {filteredTree.map((cat) => renderCategoryItem(cat, 0))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* RIGHT COLUMN: Document Preview + Petition Form */}
        <Grid item xs={12} lg={5} xl={4.5}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* 1. PREVIEW PANEL */}
            <Card
              elevation={0}
              sx={{
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)',
                display: 'flex',
                flexDirection: 'column',
                height: 480,
                overflow: 'hidden',
              }}
            >
              {/* Preview Header */}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                  {selectedDoc ? getDocumentIcon(selectedDoc.filePath || selectedDoc.name) : <Article sx={{ color: '#64748b' }} />}
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: '#1e293b',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {selectedDoc ? selectedDoc.name : 'Previsualización de Documento'}
                  </Typography>
                </Box>

                {selectedDoc && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Tooltip title="Abrir documento completo">
                      <IconButton
                        size="small"
                        href={CorporateDocumentService.getDocumentFileUrl(selectedDoc.id, false)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: '#0284c7' }}
                      >
                        <Launch fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Descargar">
                      <IconButton
                        size="small"
                        href={CorporateDocumentService.getDocumentFileUrl(selectedDoc.id, true)}
                        download
                        sx={{ color: '#0284c7' }}
                      >
                        <Download fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>

              {/* Preview Body */}
              <Box sx={{ flex: 1, position: 'relative', bgcolor: '#f1f5f9' }}>
                {selectedDoc ? (
                  <>
                    {previewLoading && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(255, 255, 255, 0.85)',
                          zIndex: 2,
                        }}
                      >
                        <CircularProgress size={30} sx={{ color: '#257eb9' }} />
                        <Typography variant="caption" sx={{ mt: 1.5, color: '#64748b' }}>
                          Cargando previsualización...
                        </Typography>
                      </Box>
                    )}
                    <iframe
                      key={selectedDoc.id}
                      title="Vista previa del documento"
                      src={CorporateDocumentService.getDocumentFileUrl(selectedDoc.id, false)}
                      width="100%"
                      height="100%"
                      style={{ border: 'none' }}
                      onLoad={() => setPreviewLoading(false)}
                      onError={() => setPreviewLoading(false)}
                    />
                  </>
                ) : (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 3,
                      textAlign: 'center',
                    }}
                  >
                    <Article sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#475569' }}>
                      Ningún documento seleccionado
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5, maxWidth: 300 }}>
                      Haz clic en cualquier documento del árbol de la izquierda para previsualizarlo aquí.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Card>

            {/* 2. PETITION CARD */}
            <Card
              elevation={0}
              sx={{
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)',
                p: { xs: 2, sm: 2.5 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <HelpOutline sx={{ color: '#257eb9', fontSize: '1.4rem' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  ¿No encuentras lo que buscas?
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ color: '#64748b', mb: 2, fontSize: '0.86rem' }}>
                Envía una petición para que se publique el documento que necesitas. Llegará directamente al Departamento de Marketing.
              </Typography>

              {petitionSuccess && (
                <Alert
                  icon={<CheckCircleOutline fontSize="inherit" />}
                  severity="success"
                  sx={{ mb: 2, borderRadius: '8px' }}
                >
                  ¡Petición enviada con éxito! La revisaremos para publicarla lo antes posible.
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmitPetition}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Escribe tu petición con el mayor detalle posible (nombre del documento, producto, catálogo...)"
                  value={petitionMessage}
                  onChange={(e) => setPetitionMessage(e.target.value)}
                  disabled={submittingPetition}
                  InputProps={{
                    sx: {
                      borderRadius: '10px',
                      fontSize: '0.88rem',
                      bgcolor: '#f8fafc',
                      '& fieldset': { borderColor: '#cbd5e1' },
                      '&:hover fieldset': { borderColor: '#257eb9' },
                      '&.Mui-focused fieldset': { borderColor: '#257eb9' },
                    },
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.8 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submittingPetition || !petitionMessage.trim()}
                    startIcon={submittingPetition ? <CircularProgress size={18} color="inherit" /> : <Send />}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2.5,
                      py: 0.9,
                      bgcolor: '#257eb9',
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: '#1b6393',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {submittingPetition ? 'Enviando...' : 'Enviar petición'}
                  </Button>
                </Box>
              </Box>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
